import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { authClient } from "@/lib/auth/auth-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthSplitShell } from "@/components/auth/AuthSplitShell";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/auth/mfa-setup")({
  head: () => ({ meta: [{ title: "Set up two-factor authentication — Heallexa" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: MfaSetupPage,
});

/**
 * Opt-in TOTP enrollment, backed by Amazon Cognito (software token MFA).
 *
 *   start  → twoFactor.enable() → AssociateSoftwareToken → { totpURI }
 *   scan   → render QR from totpURI
 *   verify → twoFactor.verifyEnrollment({ code }) → VerifySoftwareToken +
 *            SetUserMFAPreference(enabled); future logins are then gated.
 *
 * Cognito authorizes enrollment with the user's access token (no password
 * prompt — works for Google-only users too) and does NOT issue backup codes
 * (recovery is via admin reset). Already-enrolled users land on a
 * "manage / disable" view.
 */
type Step = "start" | "scan" | "done";

// Spec recipes (presentation only) — merged over shadcn defaults via twMerge.
const INPUT_CLS =
  "h-11 text-[14px] bg-background border-border rounded-md focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20";
const LABEL_CLS = "text-[11px] uppercase tracking-[0.14em] text-muted-foreground";
const BTN_CLS = "h-11 px-5 text-[14px] rounded-md";

function MfaSetupPage() {
  const { user, loading, mfa, refreshMfa, signOut, primaryRole } = useAuth();
  const navigate = useNavigate();

  // Admins and doctors are required to keep 2FA on — they may enroll but not
  // disable, and the dashboard gate (routes/dashboard.tsx) bounces them here
  // until they do.
  const mandatory = primaryRole === "admin" || primaryRole === "doctor";

  const [step, setStep] = useState<Step>("start");
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [totpUri, setTotpUri] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate({ to: "/login" });
  }, [user, loading, navigate]);

  const begin = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: err } = await authClient.twoFactor.enable();
      if (err || !data) {
        setError(err?.message ?? "Could not start two-factor setup.");
        return;
      }
      setTotpUri(data.totpURI);
      const QRCode = (await import("qrcode")).default;
      setQrDataUrl(await QRCode.toDataURL(data.totpURI, { margin: 1, width: 220 }));
      setStep("scan");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start two-factor setup.");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const { error: err } = await authClient.twoFactor.verifyEnrollment({ code });
      if (err) {
        setError(err.message ?? "That code didn't match. Try again.");
        return;
      }
      await refreshMfa();
      setStep("done");
      toast.success("Two-factor authentication enabled");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Verification failed.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setError(null);
    try {
      const { error: err } = await authClient.twoFactor.disable();
      if (err) {
        setError(err.message ?? "Could not disable two-factor.");
        return;
      }
      await refreshMfa();
      toast.success("Two-factor authentication disabled");
      navigate({ to: "/dashboard" as any, replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not disable two-factor.");
    } finally {
      setBusy(false);
    }
  };

  const heading =
    step === "done" ? "Two-factor enabled" : mfa.enrolled ? "Two-factor is on" : "Set up two-factor";

  return (
    <AuthSplitShell>
      <div className="card-surface p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-11 w-11 shrink-0 rounded-md border border-primary/30 bg-primary/10 inline-flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-primary" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">{heading}</h1>
        </div>

        {/* Already enrolled → manage / disable */}
        {mfa.enrolled && step !== "done" ? (
          <>
            <p className="text-[14px] text-muted-foreground mb-6 mt-2">
              Your account is protected with an authenticator app. You'll be asked for a 6-digit
              code each time you sign in.
            </p>
            {error && <p className="mt-3 text-[13px] text-destructive">{error}</p>}
            {mandatory ? (
              <p className="mt-4 text-xs text-muted-foreground bg-muted/50 border border-border rounded-md px-3 py-2">
                Two-factor authentication is <span className="font-medium text-foreground">required</span> for{" "}
                {primaryRole} accounts and can't be turned off.
              </p>
            ) : (
              <Button variant="destructive" onClick={disable} disabled={busy} className={`w-full mt-5 ${BTN_CLS}`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Turn off two-factor
              </Button>
            )}
            <Button variant="ghost" onClick={() => navigate({ to: "/dashboard" as any })} className={`w-full mt-2 ${BTN_CLS}`}>
              Back to dashboard
            </Button>
          </>
        ) : step === "start" ? (
          <>
            <p className="text-[14px] text-muted-foreground mb-6 mt-2">
              Add a second layer of security. After setup, you'll enter a code from your
              authenticator app (Google Authenticator, 1Password, Authy…) each time you sign in.
            </p>
            {mandatory && (
              <p className="-mt-3 mb-6 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 border border-amber-300 rounded-md px-3 py-2">
                Two-factor authentication is required for {primaryRole} accounts. You'll need to
                finish setup before you can access your dashboard.
              </p>
            )}
            {error && <p className="mt-3 text-[13px] text-destructive">{error}</p>}
            <Button onClick={begin} disabled={busy} className={`w-full mt-5 ${BTN_CLS}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Continue
            </Button>
          </>
        ) : step === "scan" ? (
          <>
            <p className="text-[14px] text-muted-foreground mb-4 mt-2">
              Scan this QR code with your authenticator app, then enter the 6-digit code it shows.
            </p>
            {qrDataUrl && (
              <div className="flex justify-center mb-4">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={qrDataUrl} alt="Two-factor QR code" className="rounded-md border border-border" />
              </div>
            )}
            {totpUri && (
              <p className="text-[11px] text-muted-foreground break-all mb-4">
                Can't scan? Enter this secret manually:{" "}
                <code className="font-mono">{new URL(totpUri).searchParams.get("secret")}</code>
              </p>
            )}

            <div className="space-y-2">
              <Label htmlFor="code" className={LABEL_CLS}>Authenticator code</Label>
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className={`${INPUT_CLS} text-center tracking-[0.5em] text-lg font-mono`}
                autoFocus
              />
            </div>
            {error && <p className="mt-3 text-[13px] text-destructive">{error}</p>}
            <Button onClick={confirm} disabled={busy || code.length < 6} className={`w-full mt-5 ${BTN_CLS}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Verify & enable
            </Button>
          </>
        ) : (
          <>
            <p className="text-[14px] text-muted-foreground mb-6 mt-2">
              Two-factor authentication is now protecting your account. You'll be asked for a code
              the next time you sign in.
            </p>
            <Button onClick={() => navigate({ to: "/dashboard" as any, replace: true })} className={`w-full ${BTN_CLS}`}>
              Continue to dashboard
            </Button>
          </>
        )}

        <button
          type="button"
          className="mt-6 text-xs text-muted-foreground hover:text-foreground underline w-full text-center"
          onClick={async () => {
            await signOut();
            navigate({ to: "/login" });
          }}
        >
          Sign out
        </button>
      </div>
    </AuthSplitShell>
  );
}
